import React, { useState, useEffect } from 'react'
import { s3Static } from '../../../../utils/constants'
import Image from 'next/image'
import { AnimatePresence } from 'framer-motion'
import { Services } from '../../service/Services'
import { RequestProvider } from '../../../../context/RequestContext'
import { useRouter } from 'next/router'
import { Faq } from '../../../commons/Faq'
import { plumberSingaporeFaqs } from '../../../../utils/faq'
import { openModal } from '../../../../service/modal-service'
import { HandymanBooking } from './HandymanBooking'
import { WhyUs } from '@/commons/service-hub/WhyUs'
import { ReviewsSection } from '@/commons/service-hub/ReviewsSection'
import { RewardsSection } from '@/commons/service-hub/RewardsSection'
import { technicianReasons } from 'data/service-hub-content'
import { HandymanDealItems } from './HandymanDealItems'
import { PlumberServices } from './PlumberServices'
import { PriceList } from '@/commons/service-hub/PriceList'
import { HowToBook } from '@/commons/service-hub/HowToBook'
import { StickyCta } from '@/commons/service-hub/StickyCta'
import { AreasServed, RelatedLinks, technicianAreasIntro } from '@/commons/service-hub/RelatedLinks'
import { findItems, formatPrice, fromPrice } from '../../../../utils/handyman-price-data'
import {
    ELECTRICIAN_HUB,
    HANDYMAN_HUB,
    HANDYMAN_PRICE_PAGE,
    PLUMBER_HUB,
    handymanJobPages,
    jobPagePath,
    jobPagesForHub,
} from '../../../../data/handyman-job-pages'

// The "popular prices" teaser. Plumbing has no price page of its own; the full
// list is the Kitchen and Bathroom sections of the handyman price list.
const POPULAR_ITEMS = [
    'Clear clogged toilet bowl',
    'Clear clogged kitchen sink or pipe',
    'Clear clogged drainage pipe',
    'Fix water leak from toilet bowl or pipes',
    'Fix water leak from faucet, sink or pipes',
    'Replace kitchen faucet',
    'Replace toilet flush system',
    'Replace shower set',
    'Replace instant water heater',
    'Install toilet bowl set',
]

const relatedLinks = [
    ...jobPagesForHub(PLUMBER_HUB).map((slug) => ({ href: jobPagePath(slug), label: handymanJobPages[slug].breadcrumbTitle })),
    { href: jobPagePath('water-heater-installation'), label: 'Water Heater Installation', desc: 'Instant and storage heaters' },
    { href: `${HANDYMAN_PRICE_PAGE}#prices-plumbing`, label: 'Plumbing Prices', desc: 'Kitchen and bathroom price list' },
    { href: HANDYMAN_HUB, label: 'Handyman Services', desc: 'Mounting, curtains, locks and more' },
    { href: ELECTRICIAN_HUB, label: 'Electrician Services', desc: 'Lighting, power points and wiring' },
]

export const Plumber = (props) => {
    const requestCategorySettings = props.requestCategorySettings;
    const countryName = props.countryName
    const testimonials = props.testimonials
    const handymanType = props.handymanType
    const handymanPriceList = props.handymanPriceList
    const requestType = 'Handyman Services'

    const router = useRouter()

    const [ isQuickBook, setIsQuickBook ] = useState(false)
    const [ isShowDealItems, setIsShowDealItems ] = useState(false)
    const [ requestForm, setRequestForm ] = useState(false)

    // Handyman bookings always start from the priced job picker, never the
    // question wizard (which lacks most jobs and their details). A
    // ?requestForm=true link opens the picker.
    useEffect(() => {
        if (router.query.requestForm) setIsShowDealItems(true)
    }, [router.query.requestForm])

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        openModal(setRequestForm)
    }

    const plumbingItems = (handymanPriceList || []).filter(item => item.technicianType === 'Plumber')
    const popularItems = findItems(handymanPriceList, POPULAR_ITEMS, 'plumber hub')
    const lowest = fromPrice(plumbingItems)

  return (
    <>
        <RequestProvider>
            <section id="plumber-singapore" className='relative'>
                {/* Static image on every screen size. Mobile used to autoplay a
                    2.4MB video here, which was the page's LCP element. */}
                <div className="-mt-14 relative w-full aspect-21/9 sm:aspect-auto sm:h-180 overflow-hidden">
                    <Image
                        src={s3Static + "handyman/handyman-plumber-fixing-kitchen-pipes.jpg"}
                        fill
                        className="object-cover"
                        alt="Plumber fixing kitchen pipes in a Singapore home - StringsSG"
                        priority
                        sizes="100vw"
                    />
                </div>
                <div id="book" className='relative bg-gray-100 sm:bg-transparent sm:text-white w-full py-2 sm:absolute sm:top-1/2 sm:-translate-y-1/2 scroll-mt-20'>
                    <HandymanBooking requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} handymanType={handymanType}
                        setIsQuickBook={setIsQuickBook} countryName={countryName} setIsShowDealItems={setIsShowDealItems}
                        defaultServiceType="Bathroom"
                        allowedServiceTypes={["Bathroom", "Kitchen"]}
                        handymanPriceList={handymanPriceList}
                        title="Plumber Singapore: Same-Day Repairs at Fixed Prices"
                        subtitle={`Leaks, choked toilets, clogged sinks and drains, taps and heaters${lowest != null ? ` from ${formatPrice(lowest)}` : ''}. Plumbers island-wide, 8am to 10pm daily.`}/>
                </div>
            </section>
            {isShowDealItems &&
                <AnimatePresence>
                    <HandymanDealItems requestCategorySettings={requestCategorySettings} handymanPriceList={handymanPriceList} setRequestForm={setRequestForm} setIsQuickBook={setIsQuickBook} openRequestForm={openRequestForm} setIsShowDealItems={setIsShowDealItems}
                        allowedCategories={["Plumbing", "Kitchen", "Bathroom"]}/>
                </AnimatePresence>
            }
            {requestForm &&
                <Services requestType={requestType} requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} isQuickBook={isQuickBook} asSheet/>
            }
        </RequestProvider>

        <PriceList
            items={popularItems}
            title="Popular Plumber Prices in Singapore"
            subtitle="Fixed prices per job. You see the total before you confirm, with no hourly billing."
            moreHref={`${HANDYMAN_PRICE_PAGE}#prices-plumbing`}
            moreLabel="See all kitchen and bathroom prices"
        />

        <section className='max-w-5xl mx-auto py-8 px-2'>
            <div className='max-w-3xl mx-auto'>
                <header className='space-y-4 px-2'>
                    <h2 className='text-2xl md:text-3xl text-center'>
                        <strong>Affordable Plumbers Near You in Singapore</strong>
                    </h2>
                    <div className='space-y-3 text-sm leading-relaxed text-gray-600'>
                        <p>
                            StringsSG sends reliable <strong>plumbers in Singapore</strong> for leaking faucets and pipes, choked toilets,
                            clogged kitchen sinks and bathroom drains, toilet flush and toilet bowl replacement, taps, shower sets and water heaters.
                        </p>
                        <p>
                            Every job has a fixed price that you see before you confirm, so there are no hourly surprises. We work in HDB
                            flats, BTO and resale units, condominiums and landed homes island-wide, with <strong>same-day slots</strong> for
                            urgent leaks and blockages.
                        </p>
                    </div>
                </header>
            </div>
            <h2 className='text-2xl md:text-3xl text-center font-bold pt-6 pb-4'>
                Our Plumbing Services in Singapore
            </h2>
            <PlumberServices handymanPriceList={handymanPriceList}/>
        </section>

        <HowToBook serviceLabel="plumber"/>
        <WhyUs title="Why Choose StringsSG Plumber Services?" reasons={technicianReasons('Plumber')}/>

        <ReviewsSection
            title="What Customers Say About Our Plumbers"
            intro={<>Real feedback from homeowners who trust <strong>StringsSG for plumbing services in Singapore</strong>.</>}
            testimonials={testimonials} />

        <AreasServed title="Plumber Near You, Island-Wide" intro={technicianAreasIntro('plumber')}/>

        <RewardsSection serviceNoun="plumbing" />

        <section className='max-w-5xl mx-auto py-10'>
            <Faq title="Plumber FAQs" questionsAnswers={plumberSingaporeFaqs}/>
        </section>

        <RelatedLinks title="Plumbing Problems and Prices" links={relatedLinks}/>
        {!requestForm && !isShowDealItems &&
            <StickyCta onBook={() => setIsShowDealItems(true)} whatsAppText="Hi, I'd like to book a plumber."/>
        }
    </>
  )
}
